"use client";

export const runtime = "edge";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Camera, Clock, Mail, MapPin, Menu, Phone, Video, X } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/hooks/useStore";
import { getContactList } from "@/store/controller/contact.controller";
import { getProgramList } from "@/store/controller/program.controller";
import { getNavbarLinkList } from "@/store/controller/navbarLink.controller";
import { getPpdbList } from "@/store/controller/ppdb.controller";
import { PPDB_CTA_DEFAULTS } from "@/app/components/landing/ppdbCta";

const DEFAULT_NAV_LINKS = [
  { label: "Beranda", href: "/#beranda" },
  { label: "Tentang", href: "/#tentang" },
  { label: "Program", href: "/program" },
  { label: "Fasilitas", href: "/fasilitas" },
  { label: "Organisasi", href: "/#organisasi" },
  { label: "Alumni", href: "/#alumni" },
  { label: "Galeri", href: "/#galeri" },
  { label: "Pengumuman", href: "/pengumuman" },
  { label: "Berita", href: "/berita" },
];

const externalLinkProps = (href: string) =>
  /^https?:\/\//i.test(href) ? { target: "_blank", rel: "noopener noreferrer" } : {};

export default function SiteLayout({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const contact = useAppSelector((state) => state.contact.list[0]);
  const programs = useAppSelector((state) => state.program.list);
  const navbarLinks = useAppSelector((state) => state.navbarLink.list);
  const ppdbNavbarLabel = useAppSelector((state) => state.ppdb.list[0]?.navbar_cta_label) || PPDB_CTA_DEFAULTS.navbar;
  // Fall back to the built-in links until the CMS list has loaded (or if it is empty).
  const navLinks = navbarLinks.length > 0 ? navbarLinks : DEFAULT_NAV_LINKS;

  useEffect(() => {
    dispatch(getContactList({}));
    dispatch(getProgramList({}));
    dispatch(getNavbarLinkList({ limit: 100 }));
    dispatch(getPpdbList({}));
  }, [dispatch]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const socials = [
    contact?.instagram ? { label: "Instagram", href: contact.instagram, Icon: Camera } : null,
    contact?.youtube ? { label: "YouTube", href: contact.youtube, Icon: Video } : null,
  ].filter((social): social is { label: string; href: string; Icon: typeof Camera } => social !== null);

  const contactRows = [
    contact?.location ? { label: contact.location, Icon: MapPin } : null,
    contact?.telephone ? { label: contact.telephone, Icon: Phone } : null,
    contact?.email ? { label: contact.email, Icon: Mail } : null,
    contact?.operating_hours ? { label: contact.operating_hours, Icon: Clock } : null,
  ].filter((row): row is { label: string; Icon: typeof MapPin } => row !== null);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-md py-1" : "bg-white/95 backdrop-blur-sm py-2"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between gap-4">
          <Link href="/" className="flex-shrink-0">
            <img src="/images/logo.png" alt="SMAIT Ihsanul Fikri" className="h-18 w-auto object-contain" />
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={`${link.label}-${link.href}`}
                {...externalLinkProps(link.href)}
                href={link.href}
                className="text-sm font-medium text-gray-600 hover:text-green-700 px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <Link
            href="/ppdb"
            className="hidden md:inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all shadow-sm hover:shadow-md flex-shrink-0"
          >
            {ppdbNavbarLabel}
          </Link>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="xl:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="xl:hidden bg-white border-t border-gray-100 px-4 py-4">
            {navLinks.map((link) => (
              <Link
                key={`${link.label}-${link.href}`}
                {...externalLinkProps(link.href)}
                href={link.href}
                onClick={() => setIsMenuOpen(false)}
                className="block text-sm font-medium text-gray-700 hover:text-green-700 py-2.5 border-b border-gray-50 hover:bg-green-50 px-2 rounded transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <Link
              href="/ppdb"
              onClick={() => setIsMenuOpen(false)}
              className="mt-3 block w-full text-center bg-orange-500 text-white text-sm font-bold py-3 rounded-xl"
            >
              {ppdbNavbarLabel}
            </Link>
          </div>
        )}
      </header>

      {children}

      <footer className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 pt-14 pb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            <div className="lg:col-span-1">
              <img
                src="/images/logo.png"
                alt="SMAIT Ihsanul Fikri"
                className="h-14 w-auto object-contain mb-4 brightness-90"
              />
              <p className="text-sm leading-relaxed text-gray-400 mb-5">
                Sekolah Menengah Atas Islam Terpadu unggulan di Kabupaten Magelang, mencetak generasi Qurani yang
                berprestasi.
              </p>
              <div className="flex gap-3">
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-9 h-9 rounded-lg bg-white/10 hover:bg-green-700 flex items-center justify-center transition-colors"
                  >
                    <social.Icon size={16} />
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Navigasi</h4>
              <ul className="space-y-2.5">
                {navLinks.map((link) => (
                  <li key={`${link.label}-${link.href}`}>
                    <Link href={link.href} {...externalLinkProps(link.href)} className="text-sm hover:text-green-400 transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Program</h4>
              <ul className="space-y-2.5">
                {programs.map((program) => (
                  <li key={program.id}>
                    <Link href="/program" className="text-sm hover:text-green-400 transition-colors">
                      {program.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Kontak</h4>
              <div className="space-y-4">
                {contactRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <row.Icon size={15} className="flex-shrink-0 mt-0.5 text-green-500" />
                    <span className="text-gray-400 leading-relaxed">{row.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
            <p>© 2026 SMAIT Ihsanul Fikri Mungkid — Kabupaten Magelang. Hak cipta dilindungi.</p>
            <div className="flex gap-4">
              <a href="#" className="hover:text-green-400 transition-colors">
                Kebijakan Privasi
              </a>
              <a href="#" className="hover:text-green-400 transition-colors">
                Peta Situs
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
